import { f7, f7ready } from 'framework7-react';

/**
 * HIG (Nhóm E): an action menu should be a popover anchored to its trigger on
 * regular width (iPad / desktop), and a bottom action sheet on phones. F7 already
 * converts an Action Sheet to a popover when `convertToPopover` is on AND a
 * `targetEl` is provided - and its own gate only converts when
 * `device.ipad || app.width >= 768 || device.desktop` (so phones stay sheets).
 *
 * Rather than thread the click target through every `f7.actions.create(...)` call
 * (~20 sites), we track the element the user most recently pressed and inject it
 * as the default `targetEl` for every action create. One central, reversible hook.
 */

// The element under the most recent pointerdown. Captured in the CAPTURE phase so
// it's recorded before the click handler runs and opens the menu.
let lastPressedEl: HTMLElement | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    (e) => {
      const el = e.target as HTMLElement | null;
      // Anchor to the nearest interactive ancestor (the actual button/row), not a
      // stray inner <span>/<i>, so the popover points at the control.
      lastPressedEl =
        (el?.closest?.('a, button, .item-link, [role="button"]') as HTMLElement | null) ?? el;
    },
    true,
  );
}

let installed = false;

/**
 * Wrap `f7.actions.create` once so every action sheet defaults to anchoring its
 * popover at the just-pressed element. Idempotent; safe to call at startup. A
 * call that passes its own `targetEl` still wins (spread after the default).
 */
export function installActionAnchor(): void {
  if (installed) return;
  installed = true;
  f7ready(() => {
    const create = f7.actions.create.bind(f7.actions);
    f7.actions.create = ((params = {}) =>
      create({ targetEl: lastPressedEl ?? undefined, ...params })) as typeof f7.actions.create;
  });
}
